export const CANVAS_CONTENT_KIND = "netpad.canvas";
// Version 3: image element `src` may be a `netpad-blob:<blobName>` asset ref
// (see canvasImageRefs.js) instead of an inline data URL. Version 2 snapshots
// are structurally identical and parse as-is; their data-URL images migrate
// to blob refs through the background image sync.
export const CANVAS_CONTENT_VERSION = 3;

const LEGACY_CANVAS_CONTENT_VERSION = 1;
const BASE64_IMAGE_CONTENT_VERSION = 2;
const DEFAULT_VIEWPORT_COORDINATE = 0;
const DEFAULT_VIEWPORT_ZOOM = 1;

export const EMPTY_CANVAS_VIEWPORT = Object.freeze({
  x: DEFAULT_VIEWPORT_COORDINATE,
  y: DEFAULT_VIEWPORT_COORDINATE,
  zoom: DEFAULT_VIEWPORT_ZOOM,
});

const isObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);
const isCanvasKind = (content) => content.kind === CANVAS_CONTENT_KIND;
const isFiniteNumber = (value) =>
  typeof value === "number" && Number.isFinite(value);

const safeParseJson = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const createCanvasContent = (snapshot) => ({
  kind: CANVAS_CONTENT_KIND,
  version: CANVAS_CONTENT_VERSION,
  snapshot,
});

const normalizeElements = (elements) => {
  if (!Array.isArray(elements)) return [];

  return elements.filter(isObject).map((element) => ({ ...element }));
};

const normalizeViewport = (viewport) => {
  if (!isObject(viewport)) return { ...EMPTY_CANVAS_VIEWPORT };

  return {
    x: isFiniteNumber(viewport.x) ? viewport.x : EMPTY_CANVAS_VIEWPORT.x,
    y: isFiniteNumber(viewport.y) ? viewport.y : EMPTY_CANVAS_VIEWPORT.y,
    zoom: isFiniteNumber(viewport.zoom)
      ? viewport.zoom
      : EMPTY_CANVAS_VIEWPORT.zoom,
  };
};

/**
 * @typedef {{ x: number, y: number, zoom: number }} CanvasViewport
 * @typedef {{ elements: Array<Record<string, unknown>>, viewport: CanvasViewport }} CanvasSnapshot
 * @typedef {{ kind: string, version: number, snapshot: CanvasSnapshot }} CanvasContentEnvelope
 */

/**
 * @returns {CanvasSnapshot}
 */
export const createEmptyCanvasSnapshot = () => ({
  elements: [],
  viewport: { ...EMPTY_CANVAS_VIEWPORT },
});

/**
 * @param {unknown} snapshot
 * @returns {CanvasSnapshot}
 */
export const normalizeCanvasSnapshot = (snapshot) => {
  if (!isObject(snapshot)) return createEmptyCanvasSnapshot();

  return {
    elements: normalizeElements(snapshot.elements),
    viewport: normalizeViewport(snapshot.viewport),
  };
};

/**
 * @param {unknown} content
 * @returns {boolean}
 */
export const isCanvasContent = (content) => {
  const parsedContent = parseCanvasContent(content);
  return parsedContent !== null;
};

/**
 * @param {unknown} content
 * @returns {CanvasContentEnvelope | null}
 */
export const parseCanvasContent = (content) => {
  if (typeof content !== "string") return null;

  const parsedContent = safeParseJson(content);
  if (!isObject(parsedContent)) return null;

  if (!isCanvasKind(parsedContent)) return null;
  if (parsedContent.version === LEGACY_CANVAS_CONTENT_VERSION) {
    return createCanvasContent(createEmptyCanvasSnapshot());
  }
  const isCompatibleVersion =
    parsedContent.version === CANVAS_CONTENT_VERSION ||
    parsedContent.version === BASE64_IMAGE_CONTENT_VERSION;
  if (!isCompatibleVersion) return null;

  return createCanvasContent(normalizeCanvasSnapshot(parsedContent.snapshot));
};

/**
 * @param {unknown} snapshot
 * @returns {string}
 */
export const serializeCanvasContent = (snapshot) =>
  JSON.stringify(createCanvasContent(normalizeCanvasSnapshot(snapshot)));

/**
 * @returns {string}
 */
export const createEmptyCanvasContent = () =>
  serializeCanvasContent(createEmptyCanvasSnapshot());
